import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ChevronRightIcon,
  EyeIcon,
  BuildingOfficeIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

/**
 * 2026 Irish Tenant Rights Resource Page
 * Optimized for SEO and User Value
 */
const benefitCards = [
  {
    icon: ShieldCheckIcon,
    title: '6-Year TMD Tenancies',
    desc: 'Automatic enrollment after 6 months provides long-term security and ends most no-fault evictions.',
    color: 'text-blue-500'
  },
  {
    icon: BanknotesIcon,
    title: '2% National Rent Cap',
    desc: 'The Rent Pressure Zone (RPZ) system is gone. All tenants are now protected from rent increases exceeding 2% or CPI.',
    color: 'text-green-500'
  },
  {
    icon: EyeIcon,
    title: 'Public Rent Register',
    desc: 'The RTB will publish all rents. This transparency allows tenants to verify their rent is legal and appeal over-charging.',
    color: 'text-cyan-500'
  }
];

const roadmapSteps = [
  {
    year: '2026',
    target: 'TMD & 2% Rent Cap Commencement',
    status: 'All new and renewing tenancies protected.'
  },
  {
    year: '2027',
    target: 'Minimum BER C3 Requirement',
    status: 'Any new letting must meet this standard.'
  },
  {
    year: '2030',
    target: 'BER B2 Requirement (All Lettings)',
    status: 'Existing lettings must meet B2 standard to remain on market.'
  }
];

const faqItems = [
  {
    q: 'When do the new TMD rules apply to my tenancy?',
    a: 'The new Tenancies of Minimum Duration (TMD) automatically apply to any tenancy created or renewed after March 1st, 2026. If you are already in a tenancy, the TMD rules will apply once your current phase (e.g., 6-year cycle) ends and a new one begins.'
  },
  {
    q: "How can I check if my landlord is a 'Large' or 'Small' landlord?",
    a: 'Landlord classification (4+ properties vs. 3- properties) is based on the RTB register. You can check the Public Rent Register (launching Q2 2026) to determine their total holdings. This is critical for no-fault eviction rules.'
  },
  {
    q: 'Does the 2% cap apply if my rent is below the market rate?',
    a: "Yes. The 2% cap is a maximum annual increase limit nationwide. The only exception is 'substantial refurbishment' or if the property is new to the market, but even then, the cap applies after the first year."
  }
];

const TenantRights2026: React.FC = () => {
  const pageTitle = 'Irish Tenant Rights 2026 | New TMD & Rent Cap Guide';
  const description =
    'Everything Irish tenants need to know about the March 2026 reforms. Rolling 6-year TMD tenancies, the 2% national rent cap, and the Public Rent Register.';
  const canonicalUrl = 'https://rentsmart.ie/blog/IrishTenantRights2026';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <div className="bg-slate-50 relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-visible font-sans text-slate-900 selection:bg-blue-100">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta
          name="keywords"
          content="Irish tenant rights 2026, TMD Ireland, RTB rent register, 2% rent cap Ireland, tenant protections 2026, landlord-toolkit"
        />
        <link rel="canonical" href={canonicalUrl} />
        {/* Open Graph Tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Helmet>
      {/* Modern Gradient Hero */}
      <header className="relative bg-indigo-900 pt-24 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>

        <div className="relative max-w-7xl mx-auto px-6 text-center lg:text-left">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 mb-8">
            <span className="text-blue-400 text-xs font-bold uppercase tracking-widest">
              Tenant Reform 2026
            </span>
          </div>

          <h1 className="text-4xl md:text-7xl font-extrabold text-white tracking-tight leading-tight mb-8">
            The{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-200">
              2026 Tenant
            </span>{' '}
            Revolution
          </h1>

          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mb-12 leading-relaxed">
            From March 1st, 2026, the Irish rental market shifts to long-term security. Understand
            your rights under the new Tenancies of Minimum Duration (TMD) framework.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Link
              to="/renter-guide"
              className="no-underline bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-500 transition-all flex items-center justify-center shadow-lg shadow-blue-500/20"
            >
              Access Renter Guide
              <ChevronRightIcon className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </header>
      {/* Benefit Cards */}
      <section className="max-w-7xl mx-auto px-6 -mt-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {benefitCards.map((item, i) => (
            <div
              key={i}
              className="bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 group"
            >
              <item.icon
                className={`w-12 h-12 ${item.color} mb-6 transition-transform group-hover:scale-110`}
              />

              <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex flex-col lg:flex-row gap-16">
          <div className="lg:w-2/3">
            <article className="prose prose-slate prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-slate-900 border-l-4 border-indigo-500 pl-4 mb-8">
                TMD: The End of 'No-Fault' Evictions
              </h2>

              <p>
                The 2026 legislation introduces <strong>Tenancies of Minimum Duration (TMD)</strong>
                . Once you have lived in a property for 6 months, you automatically gain the right
                to remain for a 6-year cycle.
              </p>

              <div className="my-10 bg-indigo-900 text-white p-8 rounded-2xl not-prose shadow-2xl flex items-start">
                <BuildingOfficeIcon className="w-8 h-8 mr-4 text-blue-400 shrink-0" />{' '}
                {/* Added a more specific icon */}
                <div>
                  <h4 className="text-blue-400 font-bold text-lg mb-2">
                    Large vs. Small Landlords
                  </h4>

                  <p className="text-slate-300">
                    If your landlord owns 4 or more properties, they <strong>cannot</strong> evict
                    you to sell the property or move in a family member. Your tenancy remains
                    protected even if the property changes hands.
                  </p>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-slate-900 border-l-4 border-indigo-500 pl-4 mb-8">
                Rent Control: Breaking the RPZ Map
              </h2>

              <p>
                Previously, rent caps only applied in 'Rent Pressure Zones.' From March 2026, the 2%
                cap is <strong>nationwide</strong>. Every tenant in Ireland is protected from annual
                increases exceeding the lower of CPI or 2%.
              </p>

              <h2 className="text-3xl font-bold text-slate-900 border-l-4 border-indigo-500 pl-4 mb-8">
                The BER Compliance Roadmap
              </h2>

              <p>
                To ensure healthy living standards, the government is introducing minimum energy
                efficiency floors. Properties failing to meet these will require retrofitting to
                remain on the market.
              </p>
              {/* IMPROVED ROADMAP (Timeline) */}

              <div className="space-y-0 my-10 not-prose relative">
                {/* Vertical Timeline Connector */}
                <div className="absolute left-10 top-0 bottom-0 w-1 bg-indigo-100 hidden sm:block"></div>

                {roadmapSteps.map((item, i) => (
                  <div key={i} className="flex items-start py-6 relative">
                    {/* Timeline Marker (Circle on the line) */}

                    <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center shrink-0 z-10 mr-4 mt-2 border-4 border-slate-50">
                      <ChartBarIcon className="w-4 h-4" />
                    </div>

                    <div className="ml-4 flex-1 bg-white border border-slate-200 p-6 rounded-xl shadow-md transition-colors hover:border-indigo-300">
                      <div className="text-sm font-bold text-indigo-600 uppercase mb-1">
                        TARGET: End of {item.year}
                      </div>

                      <div className="font-extrabold text-slate-900 text-xl">{item.target}</div>

                      <div className="text-sm text-slate-500 mt-1">{item.status}</div>
                    </div>
                  </div>
                ))}
              </div>

              <section className="pt-24 border-t border-slate-200">
                <h2 className="text-4xl font-black text-slate-900 mb-12 italic underline decoration-blue-500">
                  FAQ: Your Rights Decoded
                </h2>
                {/* IMPROVED FAQ (Accordion) */}
                <div className="space-y-4">
                  {faqItems.map((item, i) => (
                    <details
                      key={i}
                      className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow group"
                    >
                      <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                        <h4 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                          {item.q}
                        </h4>

                        <ChevronRightIcon className="w-6 h-6 text-slate-500 group-open:rotate-90 transition-transform duration-300 shrink-0" />
                      </summary>

                      <div className="px-6 pb-6 border-t border-slate-100">
                        <p className="text-slate-600 leading-relaxed pt-2">{item.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            </article>
          </div>

          <aside className="lg:w-1/3">
            <div className="sticky top-12 space-y-8">
              <div className="bg-indigo-50 p-8 rounded-3xl border border-indigo-100 shadow-xl">
                <h3 className="text-2xl font-black text-indigo-900 mb-6">2026 Tenant Checklist</h3>

                <ul className="space-y-5 text-indigo-900/80">
                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 mr-3 mt-1 text-indigo-600 shrink-0" />{' '}
                    {/* Changed icon */}
                    <span className="text-sm font-medium">
                      Claim your €1,000 Rent Tax Credit via Revenue
                    </span>
                  </li>

                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 mr-3 mt-1 text-indigo-600 shrink-0" />{' '}
                    {/* Changed icon */}
                    <span className="text-sm font-medium">
                      Check your property's BER rating on the RTB Register
                    </span>
                  </li>

                  <li className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 mr-3 mt-1 text-indigo-600 shrink-0" />{' '}
                    {/* Changed icon */}
                    <span className="text-sm font-medium">
                      Verify your landlord's classification (Large vs Small)
                    </span>
                  </li>
                </ul>

                <Link
                  to="/renter-guide"
                  className="no-underline mt-10 w-full bg-indigo-600 text-white text-center py-4 rounded-xl font-bold text-lg hover:bg-indigo-700 transition-all block shadow-lg shadow-indigo-600/30"
                >
                  Access Renter Guide
                </Link>
              </div>

              <div className="bg-slate-900 p-8 rounded-3xl text-white">
                <h4 className="text-lg font-bold mb-4 flex items-center">
                  <ExclamationTriangleIcon className="w-6 h-6 mr-2 text-rose-500" /> RTB Enforcement
                  Power
                </h4>

                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  The RTB now has the power to issue fines of up to €15,000 for illegal evictions or
                  rent cap breaches. The 2026 budget includes a 70% increase in enforcement funding.
                </p>

                <Link
                  to="https://rtb.ie/disputes/"
                  className="text-blue-400 text-sm font-semibold hover:text-blue-300 flex items-center"
                >
                  File a Dispute <ChevronRightIcon className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <footer className="bg-white border-t border-slate-200 py-16 px-6">
        <div className="max-w-7xl mx-auto text-slate-400 text-sm">
          <p className="mb-4">
            Disclaimer: This guide is based on the General Scheme of the Residential Tenancies
            (Amendment) (No. 2) Bill 2025. Legislative details are subject to final enactment.
            Always verify your specific tenancy status with the RTB.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TenantRights2026;
